import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import type { RelationType } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import type { DocumentExpirationSeverity } from "Utils/DocumentExpiration/documentExpirationUtils";

const RELATION_RESOURCES = Resources.Form.RelationType;
const FIELD_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Form.Fields;

const DOCUMENT_ICON_COLORS: Record<
  DocumentExpirationSeverity,
  "success" | "warning" | "error"
> = {
  valid: "success",
  expiringSoon: "warning",
  expired: "error",
  missing: "error",
};

const DOCUMENT_TOOLTIP_RESOURCES: Record<DocumentExpirationSeverity, string> = {
  valid: FIELD_RESOURCES.RelatedPartyFields.Legend.Documents,
  expiringSoon: FIELD_RESOURCES.RelatedPartyFields.DocumentsExpiringSoon,
  expired: FIELD_RESOURCES.RelatedPartyFields.DocumentsExpired,
  missing: FIELD_RESOURCES.RelatedPartyFields.DocumentsValidityMissing,
};

type Props = {
  fullName: string;
  relationType: RelationType;
  hasIdentificationsUploaded: boolean;
  isOwner: boolean;
  documentExpirationSeverity: DocumentExpirationSeverity;
};

export const RelatedPartySummaryContent = ({
  fullName,
  relationType,
  hasIdentificationsUploaded,
  isOwner,
  documentExpirationSeverity,
}: Props) => {
  const { t } = useResource();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      width="100%"
      ml={1}
      mr={2}
    >
      <Stack gap={0.5} width="100%">
        <Typography fontSize="0.875rem" fontWeight={400}>
          {fullName}
        </Typography>
        <Typography fontSize="0.75rem" color="text.secondary">
          {t(RELATION_RESOURCES[relationType])}
        </Typography>
      </Stack>
      <Box display="flex" gap={2}>
        {hasIdentificationsUploaded && (
          <Tooltip
            title={t(DOCUMENT_TOOLTIP_RESOURCES[documentExpirationSeverity])}
          >
            <BadgeOutlined
              color={DOCUMENT_ICON_COLORS[documentExpirationSeverity]}
            />
          </Tooltip>
        )}
        {isOwner && (
          <Tooltip title={t(FIELD_RESOURCES.IsOwner)}>
            <BusinessCenterIcon color="primary" />
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
};
